import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as tcRenewalAction from 'features/tc-renewal/duck';
import { Input } from 'shared/components';
import { validationRules, errorStrings } from 'shared/validations';
import { ValidationService } from 'shared/services';
import { getAlternateContactType } from 'features/tc-renewal/selectors';
import Cleave from 'cleave.js/react';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import replace from 'lodash/replace';
import isArray from 'lodash/isArray';
import { withTranslation } from 'react-i18next';
import './TcAlternateContact.scss';

const rules = pick(validationRules, 'emailAddress', 'firstName', 'lastName', 'cellPhone');
const errStr = pick(errorStrings, 'emailAddress', 'firstName', 'lastName', 'cellPhone');

class TCAlternateContacts extends Component {
  constructor(props) {
    super(props);

    this.state = {
      user: {
        id: '',
        emailAddress: '',
        firstName: '',
        lastName: '',
        cellPhone: '',
      },
      errors: {},
      isDirty: false,
    };

    this.handleContinue = this.handleContinue.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onPhoneChange = this.onPhoneChange.bind(this);
    this.checkIsDirty = this.checkIsDirty.bind(this);
  }

  componentDidMount() {
    const { actions, alternateContacts, contactTypes } = this.props;

    if (!isEmpty(alternateContacts) && isEmpty(this.state.id)) {
      const alternateContact = isArray(alternateContacts)
        ? alternateContacts[0]
        : alternateContacts;

      this.setState({
        user: {
          id: alternateContact.id,
          emailAddress: alternateContact.emailAddress,
          firstName: alternateContact.firstName,
          lastName: alternateContact.lastName,
          cellPhone: alternateContact.phone,
        },
        errors: {},
      });
    }

    if (!contactTypes.length) {
      actions.getContactTypes();
    }
  }

  componentDidUpdate(prevProps) {
    const { actions, alternateContacts, alternateContactsLoaded, alternateContactType, tcCycle } =
      this.props;
    const { isDirty } = this.state;

    if (
      !alternateContactsLoaded &&
      !isEmpty(alternateContactType) &&
      !isEmpty(tcCycle) &&
      (prevProps.alternateContactsLoaded !== alternateContactsLoaded ||
        prevProps.alternateContactType !== alternateContactType)
    ) {
      actions.getAlternateContacts(tcCycle.id, alternateContactType.id);
    }

    if (
      !isEmpty(alternateContacts) &&
      !isDirty &&
      prevProps.alternateContacts !== alternateContacts
    ) {
      const alternateContact = isArray(alternateContacts)
        ? alternateContacts[0]
        : alternateContacts;

      this.setState({
        user: {
          id: alternateContact.id,
          emailAddress: alternateContact.emailAddress,
          firstName: alternateContact.firstName,
          lastName: alternateContact.lastName,
          cellPhone: alternateContact.phone,
        },
      });
    }
  }

  async handleContinue() {
    const { user } = this.state;
    const { actions, alternateContactType, tcCycle } = this.props;

    const errors = ValidationService.validate(this.state.user, rules, errStr);
    this.setState({
      errors,
    });

    if (isEmpty(errors)) {
      let updated;
      if (user.id) {
        updated = await actions.updateAlternateContacts({
          contactType: {
            id: alternateContactType.id,
          },
          testCenterCycle: {
            id: tcCycle.id,
          },
          ...user,
          phone: user.cellPhone,
        });
      } else {
        updated = await actions.setAlternateContacts({
          contactType: {
            id: alternateContactType.id,
          },
          testCenterCycle: {
            id: tcCycle.id,
          },
          ...user,
          phone: user.cellPhone,
        });
      }

      if (updated && updated.message) {
        return { error: true };
      }

      return true;
    }

    return false;
  }

  onChange(e) {
    const { name, value } = e.target;

    if (name !== 'emailAddress' && !ValidationService.letters(value)) {
      return;
    }

    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
      isDirty: true,
    }));
  }

  onPhoneChange(e) {
    const { name, rawValue } = e.target;
    let newValue = rawValue;

    if (rawValue.indexOf('+') > -1) {
      newValue = replace(rawValue, '+', '');
    }

    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        [name]: newValue,
      },
      isDirty: true,
    }));
  }

  checkIsDirty() {
    return this.state.isDirty;
  }

  render() {
    const { errors, user } = this.state;

    return (
      <div className="tc-alternate-contact user-info">
        <h4>{this.props.t('testCenter.locationsAndBackupTitle')}</h4>
        <p>{this.props.t('testCenter.locationsAndBackupSubtitle')}</p>
        <div className="list-group list-group-flush">
          <div className="list-group-item name-form-container">
            <Input
              className="input-last"
              type="text"
              name="lastName"
              label="common.labels.lastName"
              value={user.lastName}
              onChange={this.onChange}
              errors={errors.lastName}
            />
            <Input
              id="userFirstName"
              className="input-first"
              type="text"
              name="firstName"
              label="common.labels.firstName"
              value={user.firstName}
              onChange={this.onChange}
              errors={errors.firstName}
            />
          </div>
          <div className="list-group-item w-75">
            <div className="input-container">
              <label className={`${errors.cellPhone ? 'label-error' : ''} w-100 fw-bold`}>
                {this.props.t('forms.labels.phoneNumber')}{' '}
              </label>
              <Cleave
                name="cellPhone"
                className={errors.cellPhone ? 'error' : ''}
                options={{
                  phone: true,
                  phoneRegionCode: 'US',
                  delimiter: '-',
                }}
                value={user.cellPhone}
                onChange={this.onPhoneChange}
                required
              />
              {errors.cellPhone?.map((error) => (
                <span className="text-error font-size-12" key={error}>
                  {this.props.t(error)}
                </span>
              ))}
            </div>
          </div>
          <div className="list-group-item">
            <Input
              className="w-100"
              type="email"
              name="emailAddress"
              label={this.props.t('common.labels.email')}
              value={user.emailAddress}
              onChange={this.onChange}
              errors={errors.emailAddress}
            />
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    alternateContactType: getAlternateContactType(state),
    contactTypes: state.tcRenewal.contactTypes,
    alternateContacts: state.tcRenewal.alternateContacts,
    alternateContactsLoaded: state.tcRenewal.alternateContactsLoaded,
    tcCycle: state.tcRenewal.tcCycle,
  };
}

function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators({ ...tcRenewalAction }, dispatch) };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(withTranslation(null, { withRef: true })(TCAlternateContacts));

TCAlternateContacts.propTypes = {
  tcCycle: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  actions: PropTypes.shape({
    getContactTypes: PropTypes.func,
    getAlternateContacts: PropTypes.func,
    updateAlternateContacts: PropTypes.func,
    setAlternateContacts: PropTypes.func,
  }).isRequired,
  alternateContacts: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.number,
      emailAddress: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      phone: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        emailAddress: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        phone: PropTypes.string,
      })
    ),
  ]),
  contactTypes: PropTypes.arrayOf(PropTypes.shape({})),
  alternateContactsLoaded: PropTypes.bool,
  alternateContactType: PropTypes.shape({
    id: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};
